import type { ComponentChildren, RefObject } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { tapped } from "../lib/anchor.ts";
import { glyphOf } from "../lib/glyphs.ts";
import { ignore } from "../lib/ignore.ts";
import { provides } from "../lib/native.ts";
import { nativeMenu } from "../lib/nativeMenu.ts";
import { useId } from "../lib/uid.ts";
import { CheckIcon } from "./icons.tsx";
import { placePopover } from "./popover.ts";
import { FOCUS } from "./styles.ts";

/** Anything inside the menu (a segmented control, say), given a way to close it. */
interface MenuContent {
  key: string;
  content: (close: () => void) => ComponentChildren;
  group?: string;
  separated?: boolean;
}

/** An item of a menu: something to do. */
export interface MenuEntry {
  label: string;
  /** Before the label (decorative). */
  icon?: ComponentChildren;
  /** Shown in place of its icon and label (a tag, say), the label still its name. */
  display?: ComponentChildren;
  onSelect: () => void;
  current?: boolean;
  /** Items sharing a group follow a heading with its name (consecutive ones). */
  group?: string;
  /** Unique key, when labels repeat. */
  key?: string;
  /** After the label, smaller and fainter (where it's from, say). */
  detail?: ComponentChildren;
  /** It removes or deletes something: in red. */
  danger?: boolean;
  /** A line above it, setting it apart from those before (a destructive one, last). */
  separated?: boolean;
  /** Classes of its own (shown only on narrow screens, say). */
  class?: string;
}

/** What a menu lists: things to do, or anything else. */
export type MenuItem = MenuEntry | MenuContent;

const KEYS: Record<string, (i: number, n: number) => number> = {
  ArrowDown: (i, n) => (i + 1) % n,
  ArrowUp: (i, n) => (i - 1 + n) % n,
  Home: () => 0,
  End: (_, n) => n - 1,
};
const TRIGGER = `inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm hover:bg-zinc-950/5 ${FOCUS}`;

/** A menu a native app draws as its own (only things to do, each its words and icon): none with anything else. */
function plain(list: readonly MenuItem[]): readonly MenuEntry[] | null {
  const entries = list.filter((i): i is MenuEntry => "onSelect" in i && i.display === undefined);
  return provides("menus") && entries.length === list.length && entries.length > 0 ? entries : null;
}

/** `entries` in the native app's own menu, from `source` (`nativeMenu`): the one chosen done (none: let go of). */
async function openNative(source: HTMLElement, entries: readonly MenuEntry[]): Promise<void> {
  const chosen = await nativeMenu(
    source,
    entries.map((e) => ({
      label: e.label,
      glyph: e.icon ? glyphOf(e.icon) : null,
      current: e.current ?? false,
      group: e.group,
      danger: e.danger ?? false,
      separated: e.separated ?? false,
      ...(typeof e.detail === "string" ? { detail: e.detail } : {}),
    })),
  );
  if (chosen !== null) {
    entries[chosen]?.onSelect();
  }
}

const items = (menu: HTMLElement | null) => [...(menu?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ?? [])];

/** On open: place the menu by its trigger (inside the window, scrolling if it's taller) and focus the first item. */
function place(e: Event, menu: HTMLElement | null, trigger: HTMLElement | null, matchWidth: boolean) {
  if ((e as ToggleEvent).newState !== "open" || !menu || !trigger) {
    return;
  }
  placePopover(menu, trigger, { matchWidth });
  items(menu)[0]?.focus();
}

/** Arrow keys / Home / End move focus between items. */
function onKeyDown(e: KeyboardEvent) {
  const move = KEYS[e.key];
  const list = items(e.currentTarget as HTMLElement);
  if (move) {
    e.preventDefault();
    list[move(list.indexOf(document.activeElement as HTMLButtonElement), list.length)]?.focus();
  }
}

/** Menu button on the Popover API (top layer, light dismiss, Esc) with arrow-key navigation. */
export function Menu(p: {
  label: ComponentChildren;
  items: readonly MenuItem[];
  class?: string;
  /** At least as wide as its trigger (a menu that stands for the trigger, like a select's). */
  matchWidth?: boolean;
  /** The trigger's accessible name, when its label is only an icon. */
  "aria-label"?: string;
  /** The trigger's tooltip. */
  title?: string;
}) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={trigger}
        type="button"
        popovertarget={id}
        aria-haspopup="menu"
        aria-label={p["aria-label"]}
        title={p.title}
        class={p.class ?? TRIGGER}
      >
        {p.label}
      </button>
      <MenuPopover id={id} trigger={trigger} items={p.items} matchWidth={p.matchWidth ?? false} />
    </>
  );
}

/**
 * A menu's popover, for a trigger of one's own (`popovertarget` set to `id`, `aria-haspopup="menu"`): placed by it,
 * its items as a menu's. `open`: open as it appears. `source`: what a native app's menu grows out of, when the
 * trigger's only part of it (a filter pill's label, of the pill): the trigger, otherwise.
 */
export function MenuPopover(p: {
  id: string;
  trigger: RefObject<HTMLElement>;
  source?: RefObject<HTMLElement>;
  items: readonly MenuItem[];
  matchWidth?: boolean;
  "aria-label"?: string;
  open?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const iconed = p.items.some((it) => "icon" in it && it.icon);
  // Its items as they are when it opens.
  const latest = useRef(p.items);
  latest.current = p.items;
  // biome-ignore lint/correctness/useExhaustiveDependencies: only as it first appears.
  useEffect(() => {
    // In a native app that draws menus, its own (a plain one: things to do) instead, from its trigger: whatever
    // opened it (a Menu's button, a filter pill's, as it appears).
    const menu = ref.current;
    const native = (e: Event) => {
      const entries = plain(latest.current);
      const trigger = p.source?.current ?? p.trigger.current;
      if ((e as ToggleEvent).newState === "open" && entries && trigger) {
        e.preventDefault();
        openNative(trigger, entries).catch(ignore);
      }
    };
    menu?.addEventListener("beforetoggle", native);
    if (p.open) {
      menu?.showPopover();
    }
    return () => menu?.removeEventListener("beforetoggle", native);
  }, []);
  return (
    <div
      ref={ref}
      id={p.id}
      popover="auto"
      // In a native app, on its own glass (lib/nativePopover.ts), when it isn't its own menu.
      data-native-pop=""
      role="menu"
      aria-label={p["aria-label"]}
      class="fixed m-0 min-w-48 overflow-y-auto overscroll-contain rounded-md border border-zinc-200 bg-white p-1 shadow-lg android:min-w-[7rem] android:rounded-2xl android:border-transparent android:bg-zinc-100 android:p-1 android:shadow-md"
      onToggle={(e) => {
        place(e, ref.current, p.trigger.current, p.matchWidth ?? false);
      }}
      onKeyDown={onKeyDown}
    >
      {p.items.map((it, i) => [
        it.group && it.group !== p.items[i - 1]?.group ? (
          <p key={`group:${it.group}`} role="presentation" class="px-3 pt-2 pb-1 text-xs font-medium text-zinc-500">
            {it.group}
          </p>
        ) : null,
        it.separated ? (
          <hr key={`sep:${"content" in it ? it.key : (it.key ?? it.label)}`} class="my-1 border-zinc-200" />
        ) : null,
        "content" in it ? (
          <div key={it.key} role="none" class="px-3 py-1.5">
            {it.content(() => ref.current?.hidePopover())}
          </div>
        ) : (
          <button
            key={it.key ?? it.label}
            type="button"
            role="menuitem"
            aria-current={it.current || undefined}
            aria-label={it.display ? it.label : undefined}
            class={`flex w-full items-center gap-2 rounded px-3 py-1.5 text-left text-sm whitespace-nowrap hover:bg-zinc-100 focus:bg-zinc-100 focus:outline-none android:min-h-12 android:gap-3 android:rounded-xl android:active:bg-zinc-950/10 android:hover:bg-transparent ${it.danger ? "text-red-700" : "text-zinc-800"} ${it.class ?? ""}`}
            onClick={() => {
              ref.current?.hidePopover();
              // What it asks next (a confirmation) from the menu's button: this item's gone.
              tapped(p.trigger.current);
              it.onSelect();
            }}
          >
            {it.display ?? (
              <>
                {/* With icons in the menu, those without keep their place: the labels line up (as menus do). */}
                {it.icon || iconed ? (
                  // Quieter than its label; red as it is, when it destroys.
                  <span class={`flex size-4 shrink-0 items-center justify-center ${it.danger ? "" : "text-zinc-500"}`}>
                    {it.icon}
                  </span>
                ) : null}
                {it.label}
              </>
            )}
            {it.detail ? <span class="text-xs text-zinc-500">{it.detail}</span> : null}
            {/* The current one: checked at the end (bold would read as a heading, or as a different kind of item). */}
            {it.current ? <CheckIcon class="ms-auto text-zinc-500" /> : null}
          </button>
        ),
      ])}
    </div>
  );
}
